import { IconDefinition } from '@fortawesome/pro-light-svg-icons';
import { Option } from '@spike/model';
import { OverFullWindow, Spinner } from 'components/UI';

import { FunctionComponent, useState } from 'react';
import EditSetting from '../EditSetting';
import EditNotificationContent from './EditNotificationContent';
import { CustomContentNotification } from '@spike/customcontentnotification-model';
import { useDispatch, useSelector } from 'react-redux';
import {
    CustomContentNotificationState,
    CustomContentNotificationStatus,
    updateOrCreateCustomContentNotification
} from '@spike/custom-content-notification-action';
import { useSettingsApiClientWrapper } from 'hooks';
import { RootState } from 'store';
import useNonInitialEffect from '@versiondos/hooks';

export interface Props {
    notificationKey: string;
    title: string;
    subtitle: string;
    sectionSubtitle: string;
    headerIcon: IconDefinition;
    onEdit?: (emailChecked: boolean, smsChecked: boolean, noticeSelected: Option<string>, optionId: string) => void;
    showEmail?: boolean;
    showSms?: boolean;
    loading: boolean;
    fallbackContent?: Record<string, string>;
    extraPlaceholders?: Array<Option<string>>;
}

export const EditCustomContentNotification: FunctionComponent<Props> = props => {
    const dispatch = useDispatch();
    const apiClientWrapper = useSettingsApiClientWrapper();
    const [editContentKey, setEditContentKey] = useState<string>('');
    const [customContentNotification, setCustomContentNotification] = useState<CustomContentNotification>({
        key: '',
        content: '',
        name: '',
        marketplaceID: 0
    });
    const [loading, setLoading] = useState(false);

    const customContentNotificationState = useSelector<RootState, CustomContentNotificationState>(
        state => state.customContentNotifications
    );

    const handleEdit = (emailChecked: boolean, smsChecked: boolean, noticeSelected: Option<string>) => {
        props.onEdit && props.onEdit(emailChecked, smsChecked, noticeSelected, props.notificationKey);
    };

    const handleSave = () => {
        setLoading(true);
        setEditContentKey('');
        dispatch(updateOrCreateCustomContentNotification(apiClientWrapper, customContentNotification));
    };

    useNonInitialEffect(() => {
        if (
            customContentNotificationState.status === CustomContentNotificationStatus.UpdateSuccess &&
            customContentNotificationState.customContentNotification
        ) {
            setCustomContentNotification(customContentNotificationState.customContentNotification);
            setLoading(false);
        }
    }, [customContentNotificationState.status]);

    return editContentKey !== '' ? (
        <OverFullWindow
            hideHeader={false}
            showFooter={true}
            title={editContentKey.includes('email') ? 'Edit Email' : 'Preview SMS'}
            centered={true}
            onClose={() => setEditContentKey('')}
            onBack={() => setEditContentKey('')}
            onSave={() => handleSave()}
        >
            <EditNotificationContent
                notificationKey={editContentKey}
                title={props.title}
                subtitle={props.subtitle}
                headerIcon={props.headerIcon}
                sectionSubtitle={props.sectionSubtitle}
                customContentNotification={customContentNotification}
                setCustomContentNotification={setCustomContentNotification}
                fallbackContent={props.fallbackContent?.[editContentKey]}
                extraPlaceholders={props.extraPlaceholders}
            />
        </OverFullWindow>
    ) : loading ? (
        <Spinner />
    ) : (
        <EditSetting
            optionId={props.notificationKey}
            noticeOptions={[]}
            headerIcon={props.headerIcon}
            title={props.title}
            subtitle={props.subtitle}
            loading={props.loading}
            defaultOption={undefined}
            onEdit={handleEdit}
            noShowOption={true}
            showEmail={props.showEmail ?? true}
            showSms={props.showSms ?? true}
            showEditContent={true}
            onEditContent={setEditContentKey}
        />
    );
};

export default EditCustomContentNotification;
